import { useState, useEffect } from 'react'
import { useQueryClient } from '@tanstack/react-query'

import { useApiClient } from '../lib/api-client'
import { useCurrentUser } from '../lib/current-user'

import { getStatusLabel, getActionLabel, getStatusClass, isTerminalStatus } from '../lib/workflow-helpers'


interface BusinessTrip {

  id: string

  tripDate: Date

  destination: string

  purpose: string

  amount: number

  remarks?: string

  status: string

  requesterId: string

  requesterUsername?: string

  lastActionActorId?: string

  lastActionActorUsername?: string

  lastActionLabel?: string

  lastActionComment?: string

  createdAt?: Date

  updatedAt?: Date

}

interface ApprovalFormProps {
  requestId: string
  onBack: () => void
}

function ApprovalForm({ requestId, onBack }: ApprovalFormProps) {
  const [request, setRequest] = useState<BusinessTrip | null>(null)
  const [loading, setLoading] = useState(true)
  const [processing, setProcessing] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [actionComment, setActionComment] = useState<string>('')
  const [actionCommentError, setActionCommentError] = useState<string | null>(null)

  const [history, setHistory] = useState<Record<string, any>[]>([])

  const api = useApiClient()
  const queryClient = useQueryClient()
  const { roles } = useCurrentUser()

  const fetchRequest = async () => {
    try {
      const response = await api.get(`/businesstrips/${requestId}`)
      const data = response.data.data ?? response.data
      setRequest(data)
    } catch (err: any) {
      setError(err.message)
    } finally {
      setLoading(false)
    }
  }


  const fetchHistory = async () => {
    try {
      const response = await api.get(`/businesstrips/${requestId}/history`)
      setHistory(response.data.data ?? [])
    } catch (err) {
      // 履歴取得の失敗は詳細表示自体をブロックしない
      console.error('承認履歴取得エラー:', err)
    }
  }


  useEffect(() => {
    fetchRequest()
    fetchHistory()
  }, [requestId])

  const handleTransition = async (action: string, requireActionComment: boolean) => {
    if (requireActionComment && (!actionComment || actionComment.trim() === '')) {
      setActionCommentError('このアクションにはコメントが必須です')
      return
    }
    setActionCommentError(null)

    if (!confirm(`この申請を${action}しますか？`)) return

    setProcessing(true)
    setError(null)

    try {
      const response = await api.post(
        `/businesstrips/${requestId}/transition`,
        { action, actionComment: actionComment.trim() || undefined }
      )

      if (!response.data?.success) {
        throw new Error(response.data?.error?.message || '処理に失敗しました')
      }

      setActionComment('')
      await queryClient.invalidateQueries({ queryKey: ['businesstrips'] })
      await fetchRequest()
      await fetchHistory()
    } catch (err: any) {
      setError(err.message)
    } finally {
      setProcessing(false)
    }
  }

  const getAvailableActions = (currentStatus: string) => {
    const actions: { name: string; label: string; styleType: string; requireActionComment: boolean }[] = []
    if (!request) return actions




    if (currentStatus === 'draft') {

      actions.push({
        name: 'submit',
        label: '申請',
        styleType: 'success',
        requireActionComment: false
      })
    }




    if (currentStatus === 'pending_supervisor' && (["supervisor"] as string[]).some(r => roles.includes(r))) {

      actions.push({
        name: 'supervisor_approve',
        label: '承認',
        styleType: 'success',
        requireActionComment: false
      })
    }




    if (currentStatus === 'pending_supervisor' && (["supervisor"] as string[]).some(r => roles.includes(r))) {

      actions.push({
        name: 'supervisor_reject',
        label: '差戻し',
        styleType: 'danger',
        requireActionComment: true
      })
    }




    if (currentStatus === 'pending_supervisor') {

      actions.push({
        name: 'withdraw',
        label: '取り下げ',
        styleType: 'warning',
        requireActionComment: false
      })
    }




    if (currentStatus === 'rejected') {

      actions.push({
        name: 'resubmit',
        label: '再申請',
        styleType: 'primary',
        requireActionComment: false
      })
    }




    if (currentStatus === 'draft') {

      actions.push({
        name: 'delete',
        label: '削除',
        styleType: 'danger',
        requireActionComment: false
      })
    }



    return actions
  }

  if (loading) return <div>読み込み中...</div>
  if (error) return <div className="error">{error}</div>
  if (!request) return <div>データが見つかりません</div>

  const availableActions = getAvailableActions(request.status as string)

  return (
    <div className="approval-form">
      <h2>申請詳細</h2>

      <div className="detail-section">
        <h3>基本情報</h3>
        <dl className="detail-list">


          <dt>id</dt>

          <dd>{request.id || '-'}</dd>




          <dt>出張日</dt>

          <dd>{request.tripDate ? new Date(request.tripDate as Date).toLocaleString() : '-'}</dd>




          <dt>行き先</dt>

          <dd>{request.destination || '-'}</dd>




          <dt>目的</dt>

          <dd>{request.purpose || '-'}</dd>




          <dt>金額</dt>

          <dd>{request.amount || '-'}</dd>




          <dt>備考</dt>

          <dd>{request.remarks || '-'}</dd>




          <dt>status</dt>

          <dd>{getStatusLabel(request.status as string)}</dd>






          <dt>申請者</dt>

          <dd>{request.requesterUsername || '-'}</dd>






          <dt>対応者</dt>

          <dd>{request.lastActionActorUsername || '-'}</dd>




          <dt>対応内容</dt>

          <dd>{request.lastActionLabel || '-'}</dd>




          <dt>対応コメント</dt>

          <dd>{request.lastActionComment || '-'}</dd>







        </dl>
      </div>


      <div className="action-section">
        <h3>アクション</h3>
        {availableActions.length === 0 ? (
          <p>実行可能なアクションがありません</p>
        ) : (
          <div className="action-area">
            <div className="action-comment-input">
              <label htmlFor="transition-action-comment">
                対応コメント
              </label>
              <textarea
                id="transition-action-comment"
                value={actionComment}
                onChange={e => {
                  setActionComment(e.target.value)
                  if (actionCommentError) setActionCommentError(null)
                }}
                disabled={processing}
                rows={3}
                placeholder="コメントを入力してください"
              />
              {actionCommentError && <p className="field-error">{actionCommentError}</p>}
            </div>
            <div className="action-buttons">
              {availableActions.map((action) => (
                <button
                  key={action.name}
                  data-testid={`action-${action.name}`}
                  onClick={() => handleTransition(action.name, action.requireActionComment)}
                  disabled={processing}
                  className={`btn-style-${action.styleType}`}
                >
                  {action.label}{action.requireActionComment && <span className="required-mark"> *</span>}
                </button>
              ))}
            </div>
            {availableActions.some(a => a.requireActionComment) && (
              <p className="action-comment-legend">*印のアクションはコメント入力が必須です</p>
            )}
          </div>
        )}
      </div>


      <div className="detail-section">
        <h3>承認履歴（新しい順）</h3>
        {history.length === 0 ? (
          <p>履歴はありません</p>
        ) : (
          <ul className="approval-history-timeline" data-testid="approval-history-timeline">
            {history.map((record: any, index: number) => (
              <li key={record.id} className={`approval-history-item${index === 0 ? ' is-latest' : ''}${isTerminalStatus(record.toStatus) ? ' is-final' : ''}`}>
                <div className="approval-history-header">
                  {index === 0 && <span className="approval-history-badge">最新</span>}
                  <span className="approval-history-actor">{record.actorUsername || '-'}</span>
                  <span className="approval-history-action">{getActionLabel(record.action)}</span>
                  <span className="approval-history-time">{record.createdAt ? new Date(record.createdAt).toLocaleString() : '-'}</span>
                  {isTerminalStatus(record.toStatus) && (
                    <span className={`approval-history-final-badge ${getStatusClass(record.toStatus)}`}>{getStatusLabel(record.toStatus)}</span>
                  )}
                </div>
                {record.actionComment && (
                  <p className="approval-history-comment">{record.actionComment}</p>
                )}
              </li>
            ))}
          </ul>
        )}
      </div>


      <div className="form-actions">
        <button onClick={onBack} disabled={processing}>
          戻る
        </button>
      </div>
    </div>
  )
}

export default ApprovalForm